// /login/mfa-enroll — POST /v1/auth/mfa/enroll/start, render QR, prompt code.
// On confirm, server returns 10 recovery codes (shown ONCE on the next page).

import { ErrorBanner, FormField, LoginShell } from '@/components/LoginShell';
import { ApiError, authFetch } from '@/lib/api';
import { useOnceMountGuard } from '@/lib/auth-guards';
import { useAuth } from '@/state/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

export const Route = createFileRoute('/login/mfa-enroll')({
  component: MfaEnrollPage,
});

function MfaEnrollPage() {
  const flowSession = useAuth((s) => s.flowSession);
  const enroll = useAuth((s) => s.pendingEnroll);
  const setFlow = useAuth((s) => s.setFlow);
  const setEnroll = useAuth((s) => s.setEnroll);
  const setRecoveryCodes = useAuth((s) => s.setRecoveryCodes);
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useOnceMountGuard(() => useAuth.getState().flowSession != null, { to: '/login' });

  // Fetch the QR + secret once if we don't already have it.
  useEffect(() => {
    if (!flowSession || enroll) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await authFetch<{
          secret_base32: string;
          otpauth_uri: string;
          qr_svg: string;
          flow_session: string;
        }>('/v1/auth/mfa/enroll/start', { method: 'POST', body: { flow_session: flowSession } });
        if (cancelled) return;
        setEnroll({
          secret_base32: data.secret_base32,
          otpauth_uri: data.otpauth_uri,
          qr_svg: data.qr_svg,
        });
        setFlow(data.flow_session, 'mfa_enroll_required');
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof ApiError ? e.code : 'internal_error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // setEnroll, setFlow intentionally omitted — Zustand returns stable refs
    // but eslint can't tell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowSession, enroll]);

  async function onConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!flowSession) return;
    setErr(null);
    setBusy(true);
    try {
      const data = await authFetch<{ flow_session: string; recovery_codes: string[] }>(
        '/v1/auth/mfa/enroll/confirm',
        { method: 'POST', body: { flow_session: flowSession, totp_code: code } },
      );
      setRecoveryCodes(data.recovery_codes);
      setFlow(data.flow_session, 'mfa_required');
      setEnroll(null);
      void navigate({ to: '/login/recovery-codes' });
    } catch (e) {
      setErr(e instanceof ApiError ? e.code : 'internal_error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoginShell
      step="STEP 3 / 4 · ENROLL AUTHENTICATOR"
      title="Add an authenticator app"
      sub="Scan the QR with Authy, 1Password, Google Authenticator, or any TOTP app. Enter the 6-digit code shown to confirm."
    >
      <ErrorBanner code={err} />
      {!enroll ? (
        <div
          className="meta mono"
          style={{ height: 192, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          generating secret…
        </div>
      ) : (
        <>
          <div
            style={{
              border: '1px solid var(--bd)',
              padding: 12,
              display: 'flex',
              justifyContent: 'center',
              background: 'var(--bg-pane-2)',
            }}
            // The QR SVG is server-generated by services/auth (qrcode lib). Safe.
            dangerouslySetInnerHTML={{ __html: enroll.qr_svg }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">SECRET (MANUAL)</span>
            <code
              className="mono"
              style={{
                fontSize: 11,
                padding: '4px 8px',
                background: 'var(--bg-base)',
                border: '1px solid var(--bd)',
                wordBreak: 'break-all',
              }}
            >
              {enroll.secret_base32}
            </code>
          </div>

          <form onSubmit={onConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FormField label="6-DIGIT CODE" mono>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                required
                style={{ flex: 1, fontFamily: 'var(--mono)', letterSpacing: 4, fontSize: 14 }}
              />
            </FormField>
            <button
              type="submit"
              className="btn pri"
              disabled={busy || code.length !== 6}
              style={{ height: 32, justifyContent: 'center' }}
            >
              {busy ? 'VERIFYING…' : 'CONFIRM'}
            </button>
          </form>
        </>
      )}
    </LoginShell>
  );
}
